import { useEffect, useRef, useState } from 'react';
import ErrorMessage from './ErrorMessage';
import LoadingSpinner from './LoadingSpinner';
import LogLevelBadge from './ds/LogLevelBadge';
import { ExpandIcon } from './ds/icons';
import { ContainerLog } from '@back4app2/sdk';
import Modal from "./Modal";

export type LogBoxProps = {
  errorMessage?: string;
  logs?: ContainerLog[];
  isLoading: boolean;
  canMaximize?: boolean;
  fullHeight?: boolean;
}

const ContainerLogBox = ({ errorMessage, logs, isLoading, canMaximize, fullHeight }: LogBoxProps) => {
  const [autoScroll, setAutoScroll] = useState(true);
  const [isMaximized, setIsMaximized] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false)
  const boxRef = useRef<HTMLDivElement>(null);

  const exitHandler = (e: Event) => {
    if (!document.fullscreenElement) setIsMaximized(false);
  }

  useEffect(() => {    
    document.addEventListener('fullscreenchange', exitHandler, false);
    return () => {
      document.removeEventListener('fullscreenchange', exitHandler, false);
    }
  }, []);

  useEffect(
    () => {
      if (!errorMessage && logs && autoScroll && boxRef.current) {
        boxRef.current.scrollTop = boxRef.current.scrollHeight;
      }
    },
    [
      errorMessage,
      logs,
      autoScroll
    ]
  );

  const onScroll = () => {
    if (boxRef.current && (boxRef.current.scrollTop + boxRef.current.clientHeight) >= 0.99 * boxRef.current.scrollHeight && !autoScroll) {
      setAutoScroll(true);
    } else if (boxRef.current && (boxRef.current.scrollTop + boxRef.current.clientHeight) < 0.99 * boxRef.current.scrollHeight && autoScroll) {
      setAutoScroll(false);
    }
  };

  const handleFullScreen = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (isLoading || errorMessage) {
      return;
    }
    e.preventDefault();
    if (boxRef.current) {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
      try {
        await boxRef.current.requestFullscreen();
        setIsMaximized(true);
      } catch (err) {
        console.log('err in requesting div fullscreen', err);
        setIsModalOpen(true);
      }
    }
  }

  const exitFullScreen = async () => {
    if (document.fullscreenElement) {
      await document.exitFullscreen();
    }
    setIsMaximized(false);
  }

  /**
   * Runtime lines carry no level of their own — only the stream they came
   * from. stderr reads as an error, stdout as system output.
   */
  const renderLines = () => (
    <div className="flex flex-col">
      {logs!.map((log, i) => (
        <div
          key={log.id}
          className={[
            'flex flex-col gap-1 px-3.5 py-[5px] sm:flex-row sm:items-start sm:gap-3',
            i % 2 ? 'bg-white/[0.015]' : '',
          ].join(' ')}
        >
          <span className="font-courrier text-ds-115 text-navy-300 flex-none sm:w-[132px]">
            {log.time.toISOString().slice(11, 23)}
          </span>
          <LogLevelBadge level={log.source === 'stderr' ? 'error' : 'system'} fixedWidth />
          <span className="font-courrier text-ds-115 text-navy-300 flex-none hidden lg:inline">
            {log.container.id.split('-')[0]}
          </span>
          <span className="font-courrier text-ds-115 leading-[1.5] text-navy-100 flex-1 min-w-0 break-words">
            {log.message}
          </span>
        </div>
      ))}
    </div>
  );

  const emptyState = (
    <div className="flex h-full justify-center items-center py-10">
      <span className="text-ds-135 text-navy-300">No log lines yet.</span>
    </div>
  );

  const body = isLoading ? (
    <div className="flex h-full justify-center items-center py-10">
      <div className="flex flex-col justify-center items-center">
        <LoadingSpinner />
        <div className="mt-4 text-ds-135 text-navy-200">Loading logs</div>
      </div>
    </div>
  ) : errorMessage ? (
    <ErrorMessage message={errorMessage} />
  ) : logs && logs.length > 0 ? renderLines() : emptyState;

  return (<div className={`relative ${fullHeight ? 'h-full' : ''}`}>
    {canMaximize && !isMaximized ? (
      <button
        type="button"
        aria-label="Expand log"
        onClick={isMaximized ? exitFullScreen : handleFullScreen}
        className="b4-focus z-10 absolute top-2.5 right-2.5 w-[30px] h-[30px] rounded-control border border-navy-400 bg-navy-700 text-navy-100 flex items-center justify-center hover:bg-navy-600 transition-colors duration-[120ms]"
      >
        <ExpandIcon size={14} />
      </button>
    ) : null}
    <div
      className="relative py-1.5 bg-navy-950 overflow-y-auto b4-thin"
      ref={boxRef}
      onScroll={onScroll}
      style={{ height: fullHeight ? '100%' : '26rem' }}
    >
      {isMaximized ? (
        <button
          type="button"
          onClick={exitFullScreen}
          className="b4-focus z-10 sticky top-[calc(100%-25px)] left-full text-ds-115 rounded-control border border-navy-400 bg-navy-700 text-navy-100 px-2 py-1"
        >
          Exit FullScreen
        </button>
      ) : null}
      {body}
    </div>
    {canMaximize ? (
      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)} theme='DARK'>
        <div className="bg-navy-950 rounded-card overflow-hidden">
          {logs && logs.length > 0 ? renderLines() : emptyState}
        </div>
      </Modal>
    ) : null}
  </div>);
}

export default ContainerLogBox;


